import { useState } from 'react';
import {
  Box,
  Button,
  Container,
  Grid,
  GridItem,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
  useDisclosure,
  Link,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { FaLightbulb, FaCube, FaChartLine, FaBook } from 'react-icons/fa';

const MotionBox = motion(Box);

export const CAREER_TOOLS = [
  {
    id: 'idea2app',
    name: '🚀 idea2app Platform',
    tagline: 'Get hired fast (deliver and deploy the apps like real engineers do)',
    icon: FaLightbulb,
    color: 'purple',
    details: {
      title: 'idea2app: How You\'ll Win Interviews',
      description: 'This is how real engineers work. Not tutorials. Actual product thinking.',
      highlights: [
        '💡 Idea → Requirements: Think like a product manager (recruiters love this)',
        '🎨 Requirements → Design: Sketch actual architecture (shows you know systems)',
        '💻 Design → Code: Build with AI tools (like the top 10% of devs do)',
        '🌍 Code → Deploy: Ship to real servers (not localhost)',
      ],
      keyBenefit: 'You\'ll build 5-6 real apps in 24 weeks. When you interview, you won\'t say "I made a todo app." You\'ll say "I shipped a RAG chatbot with 500+ users" or "I deployed a production ML pipeline." That\'s the difference between offers and rejections.',
      cta: null,
    },
  },
  {
    id: 'codeversementor',
    name: '🧠 CodeverseMentor (VS Code)',
    tagline: 'Your AI mentor lives in your editor. Get unstuck without spoilers.',
    icon: FaCube,
    color: 'blue',
    details: {
      title: 'CodeverseMentor: Code Fast, Learn Faster',
      description: 'This is how you interview-proof yourself. Step-by-step guidance + speed.',
      highlights: [
        '📋 Step-by-step execution: Breaks projects into small wins (feels good + builds confidence)',
        '💡 Smart hints: Get unstuck WITHOUT seeing the answer (learn like a real dev)',
        '⚡ Flow state: No tab-switching. Everything in VS Code (2x faster learning)',
        '🎯 Interview skills: Practice under pressure, get feedback, improve (just like real interviews)',
      ],
      keyBenefit: 'Most devs waste 40% of time being stuck or distracted. CodeverseMentor keeps you in flow. You code faster, learn deeper, and when you interview, your speed will impress them. They ask you to code something live? You won\'t panic.',
      cta: {
        text: 'Get CodeverseMentor Free',
        href: 'https://marketplace.visualstudio.com',
      },
    },
  },
  {
    id: 'mygrowth',
    name: '📈 MyGrowth Dashboard',
    tagline: 'Your secret weapon: a profile that gets recruiters calling',
    icon: FaChartLine,
    color: 'green',
    details: {
      title: 'MyGrowth: How You\'ll Stand Out on Job Boards',
      description: 'Recruiters spend 6 seconds on your profile. Make those 6 seconds count.',
      highlights: [
        '🎯 Gap finder: Know exactly which skills separate you from your dream role',
        '💼 Profile sync: Resume + GitHub + LinkedIn + Portfolio all matching (not a mess)',
        '⭐ Showcase work: Your projects, contributions, and impact (not just words)',
        '🤝 Mentor matching: Talk to engineers already at the jobs you want',
      ],
      keyBenefit: 'Top 20% of candidates get 80% of interviews. MyGrowth helps you be in that 20%. We\'ll help you show (not tell) that you\'re hire-able. Your GitHub will have 5 shipped projects. Your resume will be tight. Your LinkedIn will get recruiter messages.',
      cta: {
        text: 'Coming Soon (Beta)',
        href: null,
        disabled: true,
      },
    },
  },
  {
    id: 'learning',
    name: '📚 Deep Learning Library',
    tagline: 'Understand the concepts, Crack interviews',
    icon: FaBook,
    color: 'orange',
    details: {
      title: 'Learning Library: Crack Interview Questions',
      description: 'Interviewers ask "Why?" not "What?". This teaches you the WHY.',
      highlights: [
        '🔍 Deep dives: Python closures, React rendering, database indexing (the stuff that wins interviews)',
        '🧠 Mental models: Understand not just the code, but how systems actually work',
        '🔗 Connected learning: See how concepts relate (async → promises → async-await → real-world usage)',
        '⚡ Concept first: Build foundations so you can solve anything, not just memorize',
      ],
      keyBenefit: 'Interviewer asks: "How would you optimize this database query?" Most devs freeze. You\'ll explain indexes, query plans, and trade-offs. That\'s the difference. Strong fundamentals = you can think on your feet in interviews.',
      cta: {
        text: 'Start Learning',
        href: 'https://learning.ojasamirai.com',
      },
    },
  },
];

const ToolCard = ({ tool, onOpen, onSelectTool }) => (
  <MotionBox
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    viewport={{ once: true, amount: 0.2 }}
    bg="white"
    borderLeft={`4px solid var(--chakra-colors-${tool.color}-500)`}
    p={{ base: 6, md: 8 }}
    borderRadius="lg"
    boxShadow="0 4px 12px rgba(0, 0, 0, 0.08)"
    cursor="pointer"
    _hover={{
      boxShadow: '0 12px 24px rgba(0, 0, 0, 0.15)',
      transform: 'translateY(-4px)',
    }}
  >
    <VStack align="start" spacing={4} h="full">
      <HStack spacing={3} align="flex-start">
        <Icon as={tool.icon} fontSize="24px" color={`${tool.color}.500`} />
        <Text fontSize="lg" fontWeight="700" color="brand.800">
          {tool.name}
        </Text>
      </HStack>
      <Text fontSize="sm" color="gray.600">
        {tool.tagline}
      </Text>
      <Box mt="auto" pt={4}>
        <Button
          size="sm"
          variant="ghost"
          color={`${tool.color}.600`}
          fontWeight="600"
          rightIcon={<span>→</span>}
          onClick={() => {
            onSelectTool(tool);
            onOpen();
          }}
        >
          Learn More
        </Button>
      </Box>
    </VStack>
  </MotionBox>
);

const ToolDetailModal = ({ tool, isOpen, onClose }) => {
  if (!tool) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent bg="white" borderRadius="lg">
        <ModalHeader bg={`${tool.color}.50`} borderBottomWidth="1px" borderBottomColor={`${tool.color}.200`}>
          <HStack spacing={3}>
            <Icon as={tool.icon} fontSize="24px" color={`${tool.color}.600`} />
            <Text fontSize="xl" fontWeight="700" color="brand.800">
              {tool.details.title}
            </Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody py={8}>
          <VStack align="start" spacing={6}>
            <Text fontSize="md" color="gray.700" lineHeight="1.6">
              {tool.details.description}
            </Text>

            <Box>
              <Text fontSize="sm" fontWeight="700" color="brand.800" mb={3} textTransform="uppercase" letterSpacing="0.5px">
                Key Features
              </Text>
              <VStack align="start" spacing={2}>
                {tool.details.highlights.map((highlight, idx) => (
                  <HStack key={idx} spacing={3} align="flex-start">
                    <Box w={2} h={2} bg={`${tool.color}.500`} borderRadius="full" mt={1.5} flexShrink={0} />
                    <Text fontSize="sm" color="gray.700">
                      {highlight}
                    </Text>
                  </HStack>
                ))}
              </VStack>
            </Box>

            <Box bg={`${tool.color}.50`} p={5} borderRadius="md" borderLeft={`4px solid var(--chakra-colors-${tool.color}-500)`}>
              <Text fontSize="sm" color="gray.700" fontWeight="500" lineHeight="1.6">
                <strong>Why This Matters:</strong> {tool.details.keyBenefit}
              </Text>
            </Box>

            {tool.details.cta && (
              <Button
                as={tool.details.cta.disabled ? 'button' : Link}
                href={tool.details.cta.href}
                isExternal
                bg={`${tool.color}.500`}
                color="white"
                w="full"
                fontWeight="600"
                isDisabled={tool.details.cta.disabled}
                _hover={!tool.details.cta.disabled ? { bg: `${tool.color}.600` } : {}}
              >
                {tool.details.cta.text}
              </Button>
            )}
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const GetHiredPlatforms = ({ data }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedTool, setSelectedTool] = useState(null);

  const handleSelectTool = (tool) => {
    setSelectedTool(tool);
  };

  return (
    <Box bg="white" py={{ base: 12, md: 16 }}>
      <Container maxW="6xl">
        <VStack spacing={12}>
          {/* Header */}
          <VStack spacing={4} align="center" textAlign="center" maxW="2xl">
            <Text
              as="h2"
              fontSize={{ base: '2xl', md: '3xl' }}
              fontWeight="900"
              color="brand.800"
            >
              {data.title}
            </Text>
            <Text fontSize={{ base: 'md', md: 'lg' }} color="gray.600" lineHeight="1.6">
              {data.subtitle}
            </Text>
          </VStack>

          {/* Tools Grid */}
          <Grid
            templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}
            gap={6}
            w="full"
          >
            {CAREER_TOOLS.map((tool) => (
              <GridItem key={tool.id}>
                <ToolCard
                  tool={tool}
                  onOpen={onOpen}
                  onSelectTool={handleSelectTool}
                />
              </GridItem>
            ))}
          </Grid>
        </VStack>
      </Container>

      {/* Modal */}
      <ToolDetailModal tool={selectedTool} isOpen={isOpen} onClose={onClose} />
    </Box>
  );
};

export default GetHiredPlatforms;
